import React, { useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useGetBookingListQuery,
  usePartialUpdateBookingMutation,
} from '../../../Bookings/Service/extendedBookingsApi';
import { useDeleteBookingMutation } from '../../../Bookings/Service/bookingsApi';
import DataTable, { ColumnConfig, ColumnType } from '../../../Shared/Components/DataTable';
import { formatDate } from '../../../Shared/Helpers/formatDate';
import CancelledBookingChip, { CANCELLED_ROW_SX } from '../../../Shared/Components/CancelledBookingChip/CancelledBookingChip';
import { BookingsRoutes } from '../../../../Router/path';
import { Booking } from '../../../Shared/Types/bookingTypes';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';

interface OfferBookingsTableProps {
  offerId: number;
}

const OfferBookingsTable: React.FC<OfferBookingsTableProps> = ({ offerId }) => {
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<Partial<Booking>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
  const [bookingIdToDelete, setBookingIdToDelete] = useState<number | null>(null);
  const [updatePartialBooking] = usePartialUpdateBookingMutation();
  const [deleteBooking, { isLoading: isDeletingBooking }] = useDeleteBookingMutation();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useGetBookingListQuery({
    offerId,
    pageNumber: page + 1,
    pageSize: rowsPerPage,
    includeCancelled: true,
  });
  const baseColumns: ColumnConfig<Booking>[] = [
    {
      key: 'bookingDateFrom',
      label: 'Data rezerwacji',
      render: (row) => formatDate(row.bookingDateFrom),
    },
    {
      key: 'bookingDateTo',
      label: 'Data zakończenia rezerwacji',
      render: (row) => formatDate(row.bookingDateTo),
    },
    {
      key: 'numberOfParticipants',
      label: 'Uczestnicy',
      editable: true,
      type: ColumnType.number,
    },
    {
      key: 'price',
      label: 'Cena',
      editable: true,
      type: ColumnType.number,
      parseValue: (val) => Number(val),
    },
    {
      key: 'published',
      label: 'Opublikowana',
      editable: true,
      type: ColumnType.checkbox,
      render: (row) => (row.published ? 'Tak' : 'Nie'),
    },
    {
      key: 'possibilityOfReservation',
      label: 'Możliwość rezerwacji',
      editable: true,
      type: ColumnType.checkbox,
      render: (row) => (row.possibilityOfReservation ? 'Tak' : 'Nie'),
    },
  ];
  const hasCancelledRows = (data?.items ?? []).some((booking) => booking.cancelledAtUtc);
  const columns: ColumnConfig<Booking>[] = hasCancelledRows
    ? [
        ...baseColumns,
        {
          key: 'cancelledAtUtc',
          label: 'Status',
          render: (row) => <CancelledBookingChip cancelledAtUtc={row.cancelledAtUtc} />,
        },
      ]
    : baseColumns;

  const handleDeleteRow = async () => {
    if (!bookingIdToDelete) {
      return;
    }

    const targetBookingId = bookingIdToDelete;
    setBookingIdToDelete(null);

    try {
      await deleteBooking(targetBookingId).unwrap();
      toast.success('Termin został usunięty.');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Nie udało się usunąć terminu.'));
    }
  };

  const handleOpenDeleteDialog = (bookingId: number) => {
    setBookingIdToDelete(bookingId);
  };

  const handleCloseDeleteDialog = () => {
    setBookingIdToDelete(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading || isDeletingBooking}
        isError={isError}
        errorMessage={extractApiErrorMessage(
          error,
          'Nie udało się pobrać terminów oferty. Spróbuj ponownie później.'
        )}
        getRowId={(row) => row.id}
        getRowSx={(row) => (row.cancelledAtUtc ? CANCELLED_ROW_SX : undefined)}
        onEdit={(id) => navigate(BookingsRoutes.update.replace(':bookingId', id.toString()))}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.totalCount || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(newSize) => {
          setRowsPerPage(newSize);
          setPage(0);
        }}
        selected={selectedBookingIds}
        onSelectionChange={setSelectedBookingIds}
        editingRowId={editingRowId}
        setEditingRowId={setEditingRowId}
        editedRow={editedRow}
        setEditedRow={setEditedRow}
        onInlineSave={async (updatedRow) => {
          try {
            await updatePartialBooking(updatedRow).unwrap();
            setEditingRowId(null);
            setEditedRow({});
          } catch (error) {
            toast.error(extractApiErrorMessage(error, 'Nie udało się zapisać terminu.'));
          }
        }}
        renderCustomActions={(row) => (
          <Tooltip title="Usuń">
            <span>
              <IconButton
                size="small"
                color="error"
                sx={{ p: 0.5 }}
                disabled={isDeletingBooking}
                onClick={() => handleOpenDeleteDialog(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      />
      <Dialog
        open={bookingIdToDelete !== null}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Potwierdzenie usunięcia</DialogTitle>
        <DialogContent>Czy na pewno chcesz usunąć ten termin?</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeletingBooking}>
            Anuluj
          </Button>
          <Button
            onClick={handleDeleteRow}
            color="error"
            variant="contained"
            disabled={isDeletingBooking}
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferBookingsTable;
